import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Input from '../../components/base/Input';
import Button from '../../components/base/Button';
import { useAuth } from '../../hooks/useAuth';
import { resolveApiBaseUrl } from '../../utils/apiClient';

const API_BASE_URL = resolveApiBaseUrl();

type Category = {
  id: string;
  label: string;
  labelAr: string;
  icon: string;
  description: string;
  descriptionAr: string;
};

const categories: Category[] = [
  {
    id: 'SERVICE_QUALITY',
    label: 'Service Quality',
    labelAr: 'جودة الخدمة',
    icon: 'ri-customer-service-2-line',
    description: 'Issues with the quality of service provided',
    descriptionAr: 'مشاكل تتعلق بجودة الخدمة المقدمة',
  },
  {
    id: 'AGENT_BEHAVIOR',
    label: 'Agent Behavior',
    labelAr: 'سلوك الوكيل',
    icon: 'ri-user-star-line',
    description: 'Concerns about agent conduct or professionalism',
    descriptionAr: 'مخاوف بشأن سلوك الوكيل أو مهنيته',
  },
  {
    id: 'PROPERTY_ISSUES',
    label: 'Property Issues',
    labelAr: 'مشاكل العقار',
    icon: 'ri-home-gear-line',
    description: 'Problems related to property listings or conditions',
    descriptionAr: 'مشاكل تتعلق بإعلانات العقارات أو حالتها',
  },
  {
    id: 'PAYMENT_BILLING',
    label: 'Payment & Billing',
    labelAr: 'الدفع والفواتير',
    icon: 'ri-bank-card-line',
    description: 'Payment, billing, or financial issues',
    descriptionAr: 'مشاكل الدفع أو الفواتير أو المالية',
  },
  {
    id: 'TECHNICAL_ISSUES',
    label: 'Technical Issues',
    labelAr: 'مشاكل تقنية',
    icon: 'ri-bug-line',
    description: 'Website or app technical problems',
    descriptionAr: 'مشاكل تقنية في الموقع أو التطبيق',
  },
  {
    id: 'OTHER',
    label: 'Other',
    labelAr: 'أخرى',
    icon: 'ri-question-line',
    description: 'Other issues not listed above',
    descriptionAr: 'مشاكل أخرى غير مذكورة أعلاه',
  },
];

const priorities = [
  { id: 'LOW', label: 'Low', labelAr: 'منخفض', color: 'bg-gray-100 text-gray-700' },
  { id: 'MEDIUM', label: 'Medium', labelAr: 'متوسط', color: 'bg-blue-100 text-blue-700' },
  { id: 'HIGH', label: 'High', labelAr: 'عالي', color: 'bg-amber-100 text-amber-700' },
  { id: 'URGENT', label: 'Urgent', labelAr: 'عاجل', color: 'bg-rose-100 text-rose-700' },
];

export default function ComplaintsPage() {
  const { i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session } = useAuth();
  const isRTL = i18n.dir() === 'rtl';

  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    description: '',
    priority: 'MEDIUM',
    propertyId: searchParams.get('propertyId') || '',
    agentId: searchParams.get('agentId') || '',
    companyId: searchParams.get('companyId') || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [submittedId, setSubmittedId] = useState('');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/signin?redirect=/complaints');
    }
  }, [user, navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Strip empty optional fields to avoid UUID validation errors
      const payload: Record<string, unknown> = {
        category: formData.category,
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority,
      };
      if (formData.propertyId) payload.propertyId = formData.propertyId;
      if (formData.agentId) payload.agentId = formData.agentId;
      if (formData.companyId) payload.companyId = formData.companyId;

      const response = await fetch(`${API_BASE_URL}/complaints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setSubmittedId(data.data?.id || '');
        setFormData({
          category: '',
          subject: '',
          description: '',
          priority: 'MEDIUM',
          propertyId: '',
          agentId: '',
          companyId: '',
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Failed to submit complaint');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setSubmitStatus('error');
      setErrorMessage('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className="min-h-screen bg-white"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navigation />

      {/* Hero Section */}
      <section className="bg-white py-14 border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-6 text-center sm:px-8 lg:px-12">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] px-4 py-2">
            <i className="ri-feedback-line text-lg text-[#004D40]"></i>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#003930]">
              {isRTL ? 'نظام الشكاوى' : 'Complaint System'}
            </span>
          </div>
          <h1 className="mb-3 text-3xl font-bold leading-tight text-[var(--pv-color-primary)] md:text-4xl">
            {isRTL ? 'تقديم شكوى' : 'Submit a Complaint'}
          </h1>
          <p className="text-base text-slate-500">
            {isRTL
              ? 'نحن نأخذ شكواك على محمل الجد، وسنتواصل معك خلال 1 إلى 5 أيام عمل.'
              : 'We take your concerns seriously, and we will be in touch within 1 to 5 working days.'}
          </p>
        </div>
      </section>

      <main className="py-12">
        <div className="mx-auto max-w-3xl px-6 sm:px-8 lg:px-12">
          {/* Success State */}
          {submitStatus === 'success' && (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <i className="ri-checkbox-circle-fill text-4xl text-emerald-600"></i>
              </div>
              <h2 className="mb-2 text-2xl font-bold text-emerald-900">
                {isRTL ? 'تم إرسال شكواك بنجاح!' : 'Complaint Submitted Successfully!'}
              </h2>
              <p className="mb-6 text-emerald-700">
                {isRTL
                  ? 'استلمنا شكواك، وسنتواصل معك خلال 1 إلى 5 أيام عمل.'
                  : 'We have received your complaint, and we will be in touch within 1 to 5 working days.'}
              </p>
              {submittedId && (
                <p className="mb-6 text-sm text-emerald-600">
                  {isRTL ? 'رقم التذكرة:' : 'Ticket Number:'}{' '}
                  <span className="font-mono font-bold">{submittedId.slice(-8).toUpperCase()}</span>
                </p>
              )}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button onClick={() => navigate('/complaints/my')} variant="primary">
                  <i className="ri-list-check-2 mr-2"></i>
                  {isRTL ? 'عرض شكواي' : 'View My Complaints'}
                </Button>
                <Button onClick={() => setSubmitStatus('idle')} variant="secondary">
                  <i className="ri-add-line mr-2"></i>
                  {isRTL ? 'تقديم شكوى أخرى' : 'Submit Another'}
                </Button>
              </div>
            </div>
          )}

          {/* Form */}
          {submitStatus !== 'success' && (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <h2 className="mb-6 text-xl font-bold text-[var(--pv-color-primary)]">
                {isRTL ? 'تفاصيل الشكوى' : 'Complaint Details'}
              </h2>

              {/* Reply-time notice (REGA-required) */}
              <div className="mb-6 flex items-start gap-2 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-sunken)] p-3 text-sm text-[#003930]">
                <i className="ri-time-line mt-0.5 text-[#004D40]" aria-hidden="true"></i>
                <span>
                  {isRTL
                    ? 'سنتواصل معك خلال 1 إلى 5 أيام عمل من إرسال الشكوى.'
                    : 'We will be in touch within 1 to 5 working days of submitting your complaint.'}
                </span>
              </div>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <i className="ri-error-warning-fill text-xl text-rose-600"></i>
                  <p className="flex-1 text-sm font-medium text-rose-800">{errorMessage}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="mb-3 block text-sm font-semibold text-slate-700">
                    {isRTL ? 'تصنيف الشكوى' : 'Complaint Category'}{' '}
                    <span className="text-rose-500">*</span>
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {categories.map((category) => (
                      <label
                        key={category.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-4 transition-all hover:border-[#1B7566] hover:bg-[var(--pv-color-surface-sunken)] ${
                          formData.category === category.id
                            ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)]'
                            : 'border-slate-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="category"
                          value={category.id}
                          checked={formData.category === category.id}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className="mt-1 h-4 w-4 border-slate-300 text-[#004D40] focus:ring-[#004D40]"
                          required
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <i className={`${category.icon} text-lg text-[#004D40]`}></i>
                            <span className="font-medium text-slate-900">
                              {isRTL ? category.labelAr : category.label}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            {isRTL ? category.descriptionAr : category.description}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Priority Selection */}
                <div>
                  <label className="mb-3 block text-sm font-semibold text-slate-700">
                    {isRTL ? 'الأولوية' : 'Priority'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {priorities.map((priority) => (
                      <label
                        key={priority.id}
                        className={`cursor-pointer rounded-full px-4 py-2 text-sm font-medium transition-all ${
                          formData.priority === priority.id
                            ? priority.color + ' ring-2 ring-offset-2 ring-[#004D40]'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="priority"
                          value={priority.id}
                          checked={formData.priority === priority.id}
                          onChange={(e) => handleInputChange('priority', e.target.value)}
                          className="sr-only"
                        />
                        {isRTL ? priority.labelAr : priority.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    {isRTL ? 'الموضوع' : 'Subject'} <span className="text-rose-500">*</span>
                  </label>
                  <Input
                    placeholder={isRTL ? 'اكتب موضوع الشكوى...' : 'Brief summary of your complaint...'}
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    required
                    minLength={5}
                    maxLength={200}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    {isRTL ? 'التفاصيل' : 'Details'} <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    placeholder={
                      isRTL
                        ? 'صف شكواك بالتفصيل...'
                        : 'Please describe your complaint in detail...'
                    }
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={6}
                    className="w-full resize-y rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 transition-all focus:border-[#004D40] focus:outline-none focus:ring-4 focus:ring-[#F2F8F7]"
                    required
                    minLength={20}
                    maxLength={5000}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    {formData.description.length}/5000{' '}
                    {isRTL ? 'حرف' : 'characters'}
                  </p>
                </div>

                {/* Optional Entity Links */}
                <div className="rounded-xl bg-slate-50 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-slate-700">
                    {isRTL ? 'ربط الشكوى (اختياري)' : 'Link Complaint (Optional)'}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">
                        {isRTL ? 'رقم العقار' : 'Property ID'}
                      </label>
                      <Input
                        placeholder={isRTL ? 'معرف العقار' : 'Property ID'}
                        value={formData.propertyId}
                        onChange={(e) => handleInputChange('propertyId', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">
                        {isRTL ? 'رقم الوكيل' : 'Agent ID'}
                      </label>
                      <Input
                        placeholder={isRTL ? 'معرف الوكيل' : 'Agent ID'}
                        value={formData.agentId}
                        onChange={(e) => handleInputChange('agentId', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">
                        {isRTL ? 'رقم الشركة' : 'Company ID'}
                      </label>
                      <Input
                        placeholder={isRTL ? 'معرف الشركة' : 'Company ID'}
                        value={formData.companyId}
                        onChange={(e) => handleInputChange('companyId', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <Button type="submit" disabled={isSubmitting} className="w-full" size="lg">
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      <span>{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-fill text-lg"></i>
                      <span>{isRTL ? 'إرسال الشكوى' : 'Submit Complaint'}</span>
                    </span>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              {isRTL ? 'هل لديك شكاوى سابقة؟' : 'Have previous complaints?'}{' '}
              <button
                onClick={() => navigate('/complaints/my')}
                className="font-medium text-[#004D40] hover:underline"
              >
                {isRTL ? 'عرض شكواي' : 'View My Complaints'}
              </button>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
